import React, { useState, useEffect, useRef } from 'react';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';
import { Btn as Button, Modal, Field, FormRow } from '../../components/ui';
import axios from '../../api/axios';
import toast from 'react-hot-toast';

const translations = { en, hi, gu };

const SERVICE_TYPES = ['Maid', 'Cook', 'Driver', 'Tutor', 'Caretaker', 'Nurse', 'Laundry', 'Milk Delivery', 'Newspaper Delivery', 'Electrician', 'Plumber', 'Other'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const AddEditVisitorServiceProviderModal = ({ onClose, onSuccess, providerToEdit = null }) => {
    const { language = 'en' } = useSettings();
    const t = (key, params) => {
        let str = translations[language]?.service_providers?.[key] || translations.en?.service_providers?.[key] || (typeof params === 'string' ? params : key);
        if (params && typeof params === 'object') {
            Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); });
        }
        return str;
    };

    const initialUnit = providerToEdit?.units?.[0] || {};

    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        name: providerToEdit?.name || '',
        mobile_number: providerToEdit?.mobile_number || '',
        email: providerToEdit?.email || '',
        emergency_contact_number: providerToEdit?.emergency_contact_number || '',
        address: providerToEdit?.address || '',
        service_type: providerToEdit ? (SERVICE_TYPES.includes(providerToEdit.service_type) ? providerToEdit.service_type : 'Other') : 'Maid',
        custom_service_type: providerToEdit && !SERVICE_TYPES.includes(providerToEdit.service_type) ? providerToEdit.service_type : '',
        photo: null,
        unit_id: initialUnit.unit_id || '',
        access_type: initialUnit.access_type || 'scheduled',
        start_date: initialUnit.start_date ? initialUnit.start_date.split('T')[0] : new Date().toISOString().split('T')[0],
        end_date: initialUnit.end_date ? initialUnit.end_date.split('T')[0] : '',
        is_active: initialUnit.is_active !== undefined ? initialUnit.is_active : true,
        schedules: initialUnit.schedules?.length > 0 ? initialUnit.schedules.map(s => ({
            ...s,
            entry_start_time: s.entry_start_time.substring(0, 5),
            entry_end_time: s.entry_end_time.substring(0, 5)
        })) : [
            { day_of_week: 1, entry_start_time: '08:00', entry_end_time: '18:00' }
        ]
    });
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scheduleErrors, setScheduleErrors] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [photoPreview, setPhotoPreview] = useState(providerToEdit?.photo_url || null);

    useEffect(() => {
        return () => {
            if (photoPreview && photoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreview);
            }
        };
    }, [photoPreview]);

    const setField = (key, val) => {
        setFormData(prev => ({ ...prev, [key]: val }));
        if (validationErrors[key]) {
            setValidationErrors(prev => ({ ...prev, [key]: null }));
        }
    };

    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const response = await axios.get('/api/v1/units');
                const list = response?.data || (Array.isArray(response) ? response : []);
                if (Array.isArray(list) && list.length > 0) {
                    setUnits(list);
                    if (!formData.unit_id) {
                        setFormData(prev => ({ ...prev, unit_id: prev.unit_id || list[0].id }));
                    }
                }
            } catch (err) {
                console.error("Failed to fetch units", err);
            }
        };
        fetchUnits();
    }, []);

    useEffect(() => {
        if (formData.access_type !== 'scheduled') {
            setScheduleErrors([]);
            return;
        }
        const errors = [];
        const grouped = {};
        formData.schedules.forEach(s => {
            if (!grouped[s.day_of_week]) grouped[s.day_of_week] = [];
            grouped[s.day_of_week].push(s);
        });

        for (const day in grouped) {
            const shifts = [...grouped[day]].sort((a, b) => (a.entry_start_time || '').localeCompare(b.entry_start_time || ''));
            for (let i = 0; i < shifts.length; i++) {
                const start = shifts[i].entry_start_time;
                const end = shifts[i].entry_end_time;
                
                if (!start || !end) {
                    errors.push(`${DAYS[day]}: Start and end times are required.`);
                } else if (start >= end) {
                    errors.push(`${DAYS[day]}: Start time must be earlier than end time.`);
                } else {
                    const startMins = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1]);
                    const endMins = parseInt(end.split(':')[0]) * 60 + parseInt(end.split(':')[1]);
                    if (endMins - startMins < 30) {
                        errors.push(`${DAYS[day]}: Shift from ${start} to ${end} must be at least 30 minutes.`);
                    }
                }
                
                if (i < shifts.length - 1 && end && shifts[i+1].entry_start_time) {
                    if (end > shifts[i+1].entry_start_time) {
                        errors.push(`${DAYS[day]}: Overlapping shifts detected.`);
                    }
                }
            }
        }
        setScheduleErrors([...new Set(errors)]);
    }, [formData.schedules, formData.access_type]);

    const activeDays = [...new Set(formData.schedules.map(s => s.day_of_week))].sort();
    const availableDaysToAdd = DAYS.map((_, i) => i).filter(i => !activeDays.includes(i));
    const [dayToAdd, setDayToAdd] = useState(availableDaysToAdd.length > 0 ? availableDaysToAdd[0] : 1);

    useEffect(() => {
        if (availableDaysToAdd.length > 0 && !availableDaysToAdd.includes(dayToAdd)) {
            setDayToAdd(availableDaysToAdd[0]);
        }
    }, [availableDaysToAdd, dayToAdd]);

    const handleAddDay = () => {
        if (availableDaysToAdd.length > 0) {
            const day = availableDaysToAdd.includes(dayToAdd) ? dayToAdd : availableDaysToAdd[0];
            setFormData({
                ...formData,
                schedules: [...formData.schedules, { day_of_week: day, entry_start_time: '08:00', entry_end_time: '18:00' }]
            });
        }
    };

    const handleAddShiftToDay = (dayIndex) => {
        setFormData({
            ...formData,
            schedules: [...formData.schedules, { day_of_week: dayIndex, entry_start_time: '08:00', entry_end_time: '18:00' }]
        });
    };

    const handleScheduleChange = (index, field, value) => {
        const newSchedules = [...formData.schedules];
        newSchedules[index][field] = value;
        setFormData({ ...formData, schedules: newSchedules });
    };

    const handleRemoveSchedule = (index) => {
        const newSchedules = formData.schedules.filter((_, i) => i !== index);
        setFormData({ ...formData, schedules: newSchedules });
    };

    const validateName = (name) => {
        if (!name || !name.trim()) return t("val_name_required", "Provider name is required");
        const trimmed = name.trim();
        if (/\d/.test(trimmed)) {
            return t("val_name_no_numbers", "Provider name cannot contain numbers");
        }
        if (trimmed.length < 2) {
            return t("val_name_min", "Provider name must be at least 2 characters");
        }
        if (!/^[\p{L}\s.'\-]+$/u.test(trimmed)) {
            return t("val_name_invalid", "Please enter a valid provider name");
        }
        return null;
    };

    const handleNameChange = (e) => {
        const val = e.target.value;
        setFormData(prev => ({ ...prev, name: val }));
        if (validationErrors.name) {
            setValidationErrors(prev => ({ ...prev, name: validateName(val) }));
        }
    };

    const handleNameBlur = (e) => {
        const val = e.target.value;
        setValidationErrors(prev => ({ ...prev, name: validateName(val) }));
    };

    const validateEmail = (val) => {
        if (!val || !val.trim()) return null;
        const trimmed = val.trim();
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(trimmed)) {
            return t("val_email_invalid", "Please enter a valid email address (e.g. name@example.com)");
        }
        if (trimmed.length > 255) {
            return t("val_email_max", "Email address must not exceed 255 characters");
        }
        return null;
    };

    const validateMobile = (mobile) => {
        if (!mobile || !mobile.trim()) return "Mobile number is required.";
        const clean = mobile.replace(/\s+/g, '');
        if (!/^\+?[0-9]{10,15}$/.test(clean)) {
            return "Please enter a valid 10-digit mobile number.";
        }
        return null;
    };

    const validateEmergency = (phone) => {
        if (!phone || !phone.trim()) return null;
        const clean = phone.replace(/\s+/g, '');
        if (!/^\+?[0-9]{10,15}$/.test(clean)) {
            return "Please enter a valid emergency contact number.";
        }
        return null;
    };

    const handleEmailChange = (e) => {
        const val = e.target.value;
        setFormData(prev => ({ ...prev, email: val }));
        if (!val || !val.trim()) {
            setValidationErrors(prev => ({ ...prev, email: null }));
        } else {
            setValidationErrors(prev => ({ ...prev, email: validateEmail(val) }));
        }
    };

    const handleEmailBlur = (e) => {
        const val = (e?.target?.value ?? formData.email ?? '').trim();
        setFormData(prev => ({ ...prev, email: val }));
        setValidationErrors(prev => ({ ...prev, email: validateEmail(val) }));
    };

    const handleMobileChange = (e) => {
        const val = e.target.value.replace(/[^0-9+]/g, '');
        setFormData(prev => ({ ...prev, mobile_number: val }));
        if (validationErrors.mobile_number) {
            setValidationErrors(prev => ({ ...prev, mobile_number: validateMobile(val) }));
        }
    };

    const handleMobileBlur = (e) => {
        const val = e.target.value;
        setValidationErrors(prev => ({ ...prev, mobile_number: validateMobile(val) }));
    };

    const handleEmergencyChange = (e) => {
        const val = e.target.value.replace(/[^0-9+]/g, '');
        setFormData(prev => ({ ...prev, emergency_contact_number: val }));
        if (validationErrors.emergency_contact_number) {
            setValidationErrors(prev => ({ ...prev, emergency_contact_number: validateEmergency(val) }));
        }
    };

    const handleEmergencyBlur = (e) => {
        const val = e.target.value;
        setValidationErrors(prev => ({ ...prev, emergency_contact_number: validateEmergency(val) }));
    };

    const validateForm = () => {
        const errs = {};

        if (!formData.unit_id && units.length > 0) {
            errs.unit_id = "Please select a unit.";
        }
        const nameErr = validateName(formData.name);
        if (nameErr) {
            errs.name = nameErr;
        }
        const mobileErr = validateMobile(formData.mobile_number);
        if (mobileErr) {
            errs.mobile_number = mobileErr;
        }
        const emergencyErr = validateEmergency(formData.emergency_contact_number);
        if (emergencyErr) {
            errs.emergency_contact_number = emergencyErr;
        }
        const emailErr = validateEmail(formData.email);
        if (emailErr) {
            errs.email = emailErr;
        }
        if (formData.service_type === 'Other' && !formData.custom_service_type?.trim()) {
            errs.custom_service_type = "Please specify the service type.";
        }
        if (!formData.start_date) {
            errs.start_date = "Start date is required.";
        }
        if (formData.access_type === 'temporary') {
            if (!formData.end_date) {
                errs.end_date = "End date is required for temporary access.";
            } else if (formData.start_date && new Date(formData.end_date) < new Date(formData.start_date)) {
                errs.end_date = "Access Expiry Date cannot be before Start Date.";
            }
        }

        // Validate schedule slot timings
        if (formData.access_type === 'scheduled') {
            if (scheduleErrors.length > 0) {
                errs.schedules = scheduleErrors[0];
            } else if (!formData.schedules || formData.schedules.length === 0) {
                errs.schedules = "At least one schedule shift is required for scheduled access.";
            } else {
                for (let i = 0; i < formData.schedules.length; i++) {
                    const s = formData.schedules[i];
                    if (!s.entry_start_time || !s.entry_end_time) {
                        errs.schedules = "Please specify both start and end time for each schedule slot.";
                        break;
                    }
                    if (s.entry_start_time >= s.entry_end_time) {
                        errs.schedules = "End time must be strictly after start time for each schedule slot.";
                        break;
                    }
                }
            }
        }

        setValidationErrors(errs);
        if (Object.keys(errs).length > 0) {
            const firstErr = Object.values(errs)[0];
            toast.error(Array.isArray(firstErr) ? firstErr[0] : firstErr);
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            const data = new FormData();
            data.append('name', formData.name.trim());
            data.append('mobile_number', formData.mobile_number.trim());
            if (formData.email) data.append('email', formData.email.trim());
            if (formData.emergency_contact_number) data.append('emergency_contact_number', formData.emergency_contact_number.trim());
            if (formData.address) data.append('address', formData.address.trim());
            data.append('service_type', formData.service_type === 'Other' && formData.custom_service_type 
                ? formData.custom_service_type.trim() 
                : formData.service_type);
            data.append('access_type', formData.access_type);
            data.append('start_date', formData.start_date);
            
            if (formData.access_type === 'temporary' && formData.end_date) {
                data.append('end_date', formData.end_date);
            }
            
            if (formData.unit_id) {
                data.append('unit_id', formData.unit_id);
            }
            
            if (providerToEdit) {
                data.append('is_active', formData.is_active ? 1 : 0);
            }
            
            if (formData.photo) {
                data.append('photo', formData.photo);
            }

            if (formData.access_type === 'scheduled') {
                formData.schedules.forEach((schedule, index) => {
                    data.append(`schedules[${index}][day_of_week]`, schedule.day_of_week);
                    data.append(`schedules[${index}][entry_start_time]`, schedule.entry_start_time);
                    data.append(`schedules[${index}][entry_end_time]`, schedule.entry_end_time);
                });
            }

            if (providerToEdit) {
                data.append('_method', 'PUT');
                await axios.post(`/api/v1/visitor-service-providers/${providerToEdit.id}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success(t("toast_provider_updated", "Provider updated successfully"));
            } else {
                await axios.post('/api/v1/visitor-service-providers', data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success(t("toast_provider_added", "Provider added successfully"));
            }
            if (onSuccess) onSuccess();
            if (onClose) onClose();
        } catch (error) {
            console.error("Failed to save provider:", error);
            const serverErrors = error?.errors || error?.response?.data?.errors;
            if (serverErrors) {
                const formatted = {};
                Object.keys(serverErrors).forEach(k => {
                    formatted[k] = Array.isArray(serverErrors[k]) ? serverErrors[k][0] : serverErrors[k];
                });
                setValidationErrors(formatted);
                const firstKey = Object.keys(formatted)[0];
                toast.error(formatted[firstKey] || 'Please fix the validation errors.');
            } else {
                toast.error(error?.message || error?.data?.message || 'Failed to save provider');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal title={providerToEdit ? t("title_edit_provider", "Edit Service Provider") : t("title_add_provider", "Add Service Provider")} onClose={onClose} wide>
            <form onSubmit={handleSubmit} noValidate className="space-y-4 text-slate-900 dark:text-white">
                <FormRow cols={2}>
                    <Field
                        label={t("label_select_unit", "Select Unit")}
                        type="select"
                        required
                        value={formData.unit_id || ''}
                        onChange={(e) => setField('unit_id', e.target.value)}
                        error={validationErrors.unit_id}
                        options={[
                            { value: '', label: 'Select Unit' },
                            ...units.map(unit => ({
                                value: unit.id,
                                label: `${unit.unit_number}${unit.owner_name ? ` (${unit.owner_name})` : ''}`
                            }))
                        ]}
                    />

                    <Field
                        label={t("label_name", "Name")}
                        required
                        value={formData.name}
                        onChange={handleNameChange}
                        onBlur={handleNameBlur}
                        error={validationErrors.name}
                        placeholder="Enter full name"
                    />
                </FormRow>

                <FormRow cols={2}>
                    <Field
                        label={t("label_mobile_number", "Mobile Number")}
                        type="tel"
                        required
                        value={formData.mobile_number}
                        onChange={handleMobileChange}
                        onBlur={handleMobileBlur}
                        error={validationErrors.mobile_number}
                        placeholder="e.g. 9876543210"
                    />

                    <Field
                        label={t("label_emergency_contact", "Emergency Contact Number")}
                        type="tel"
                        value={formData.emergency_contact_number}
                        onChange={handleEmergencyChange}
                        onBlur={handleEmergencyBlur}
                        error={validationErrors.emergency_contact_number}
                        placeholder="e.g. 9876543210"
                    />
                </FormRow>

                <FormRow cols={2}>
                    <Field
                        label={t("label_email_address", "Email Address")}
                        type="email"
                        value={formData.email}
                        onChange={handleEmailChange}
                        onBlur={handleEmailBlur}
                        error={validationErrors.email}
                        placeholder="name@example.com"
                    />

                    <div>
                        <Field
                            label={t("label_service_type", "Service Type")}
                            type="select"
                            value={formData.service_type}
                            onChange={(e) => setField('service_type', e.target.value)}
                            options={SERVICE_TYPES.map(type => ({
                                value: type,
                                label: t("type_" + type.toLowerCase().replace(/ /g, "_"), type)
                            }))}
                        />
                        {formData.service_type === 'Other' && (
                            <div className="mt-2">
                                <Field
                                    placeholder="Specify Service Type"
                                    required
                                    value={formData.custom_service_type}
                                    onChange={(e) => setField('custom_service_type', e.target.value)}
                                    error={validationErrors.custom_service_type}
                                />
                            </div>
                        )}
                    </div>
                </FormRow>

                <FormRow cols={1}>
                    <Field
                        label={t("label_residential_address", "Residential Address")}
                        type="textarea"
                        rows={2}
                        value={formData.address}
                        onChange={(e) => setField('address', e.target.value)}
                        error={validationErrors.address}
                        placeholder="Enter residential address"
                    />
                </FormRow>

                <FormRow cols={2}>
                    <div>
                        <label className="flex items-center gap-1 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                            <span>{t("label_provider_photo", "Provider Photo")}</span>
                            <span className="text-slate-400 dark:text-slate-500 font-normal lowercase text-[11px]">{t("label_optional", "(optional)")}</span>
                        </label>
                        <div className={`flex items-center gap-2.5 p-2 rounded-lg border bg-white dark:bg-slate-800 transition-colors ${validationErrors.photo ? 'border-red-500 ring-1 ring-red-500' : 'border-slate-200 dark:border-slate-700'}`}>
                            {photoPreview ? (
                                <div className="relative shrink-0">
                                    <img 
                                        src={photoPreview} 
                                        alt="Provider preview" 
                                        className="w-10 h-10 rounded-lg object-cover border border-slate-200 dark:border-slate-700 shadow-xs" 
                                    />
                                    {formData.photo && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setField('photo', null);
                                                setPhotoPreview(providerToEdit?.photo_url || null);
                                                if (fileInputRef.current) fileInputRef.current.value = '';
                                            }}
                                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow-sm hover:bg-red-600 transition-colors"
                                            title="Remove selected photo"
                                        >
                                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700/60 border border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-400 shrink-0">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            )}
                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                                <input
                                    ref={fileInputRef}
                                    id="provider-photo-upload"
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files[0] || null;
                                        setField('photo', file);
                                        if (file) {
                                            setPhotoPreview(URL.createObjectURL(file));
                                        } else {
                                            setPhotoPreview(providerToEdit?.photo_url || null);
                                        }
                                    }}
                                />
                                <div className="flex items-center gap-2">
                                    <label
                                        htmlFor="provider-photo-upload"
                                        className="cursor-pointer inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-primary text-white hover:bg-primary-dark transition-colors shrink-0 shadow-2xs"
                                    >
                                        <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span>{formData.photo ? t("btn_change_file", "Change file") : t("btn_choose_file", "Choose file")}</span>
                                    </label>
                                    {formData.photo && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setField('photo', null);
                                                setPhotoPreview(providerToEdit?.photo_url || null);
                                                if (fileInputRef.current) fileInputRef.current.value = '';
                                            }}
                                            className="text-xs text-red-500 hover:text-red-700 font-medium shrink-0"
                                            title="Remove photo"
                                        >
                                            {t("btn_remove", "Remove")}
                                        </button>
                                    )}
                                </div>
                                <p 
                                    className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-1 select-none"
                                    title={formData.photo ? formData.photo.name : (photoPreview ? 'Current photo attached' : 'No file chosen')}
                                >
                                    {formData.photo ? formData.photo.name : (photoPreview ? t("text_photo_attached", "Current photo attached") : t("text_no_file_chosen", "No file chosen"))}
                                </p>
                            </div>
                        </div>
                        {validationErrors.photo && (
                            <p className="text-xs text-red-500 font-medium mt-1">{validationErrors.photo}</p>
                        )}
                    </div>

                    <Field
                        label={t("label_access_type", "Access Type")}
                        type="select"
                        required
                        value={formData.access_type}
                        onChange={(e) => setField('access_type', e.target.value)}
                        options={[
                            { value: 'scheduled', label: t("opt_scheduled", "Scheduled") },
                            { value: 'unrestricted', label: 'Permanent' },
                            { value: 'temporary', label: 'Temporary' }
                        ]}
                    />
                </FormRow>

                <FormRow cols={formData.access_type === 'temporary' ? 2 : 1}>
                    <Field
                        label={t("label_start_date", "Start Date")}
                        type="date"
                        required
                        value={formData.start_date}
                        onChange={(e) => setField('start_date', e.target.value)}
                        error={validationErrors.start_date}
                    />

                    {formData.access_type === 'temporary' && (
                        <Field
                            label={t("label_end_date", "End Date")}
                            type="date"
                            required
                            min={formData.start_date}
                            value={formData.end_date}
                            onChange={(e) => setField('end_date', e.target.value)}
                            error={validationErrors.end_date}
                        />
                    )}
                </FormRow>

                {providerToEdit && (
                    <div className="flex items-center justify-between p-3 sm:p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/50 transition-colors">
                        <div className="flex flex-col pr-3">
                            <span className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200">
                                Enable Access for this Provider
                            </span>
                            <span className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                Active providers can be permitted entry at security gates
                            </span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input 
                                type="checkbox" 
                                id="is_active"
                                className="sr-only peer"
                                checked={formData.is_active}
                                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                            />
                            <div className="w-11 h-6 bg-slate-300 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                    </div>
                )}

                {formData.access_type === 'scheduled' && (
                    <div className="border border-slate-200 dark:border-slate-700 p-2.5 sm:p-4 rounded-xl bg-slate-50/50 dark:bg-slate-800/40">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-3 border-b border-slate-200 dark:border-slate-700 pb-2.5">
                            <div>
                                <h3 className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200">
                                    {t("title_weekly_schedule", "Weekly Schedule")}
                                </h3>
                                <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    Configure allowed entry days and shift timings
                                </p>
                            </div>
                            {availableDaysToAdd.length > 0 && (
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <select 
                                        className="flex-1 sm:flex-initial px-2.5 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        value={dayToAdd}
                                        onChange={(e) => setDayToAdd(parseInt(e.target.value))}
                                    >
                                        {availableDaysToAdd.map(i => (
                                            <option key={i} value={i}>{t("day_" + DAYS[i].toLowerCase(), DAYS[i])}</option>
                                        ))}
                                    </select>
                                    <Button type="button" variant="outline" size="sm" onClick={handleAddDay} className="shrink-0 text-xs py-1.5">
                                        + Add Day
                                    </Button>
                                </div>
                            )}
                        </div>

                        {validationErrors.schedules && (
                            <p className="text-xs text-red-500 font-medium mb-2.5">
                                {Array.isArray(validationErrors.schedules) ? validationErrors.schedules[0] : validationErrors.schedules}
                            </p>
                        )}

                        {scheduleErrors.length > 0 && (
                            <div className="mb-3.5 p-2.5 sm:p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-xs border border-red-100 dark:border-red-800">
                                <ul className="list-disc pl-4 space-y-1">
                                    {scheduleErrors.map((err, idx) => <li key={idx}>{err}</li>)}
                                </ul>
                            </div>
                        )}

                        <div className="space-y-3">
                            {activeDays.map(dayIndex => {
                                const daySchedules = formData.schedules
                                    .map((s, i) => ({ ...s, originalIndex: i }))
                                    .filter(s => s.day_of_week === dayIndex);

                                return (
                                    <div key={dayIndex} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-xs">
                                        <div className="bg-slate-50 dark:bg-slate-800/80 px-2.5 sm:px-3.5 py-1.5 sm:py-2 text-xs font-semibold flex justify-between items-center border-b border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                                                {t("day_" + DAYS[dayIndex].toLowerCase(), DAYS[dayIndex])}
                                            </span>
                                            <Button 
                                                type="button" 
                                                variant="ghost" 
                                                size="sm" 
                                                className="text-xs text-primary hover:text-primary-focus h-auto py-0.5 px-2 font-medium" 
                                                onClick={() => handleAddShiftToDay(dayIndex)}
                                            >
                                                + Add Shift
                                            </Button>
                                        </div>
                                        <div className="p-2 sm:p-3 space-y-2">
                                            {daySchedules.map((schedule) => (
                                                <div key={schedule.originalIndex} className="flex gap-1.5 sm:gap-2 items-center bg-slate-50/70 dark:bg-slate-800/40 p-1.5 sm:p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                                                    <input
                                                        type="time"
                                                        value={schedule.entry_start_time}
                                                        onChange={(e) => handleScheduleChange(schedule.originalIndex, 'entry_start_time', e.target.value)}
                                                        className="min-w-0 flex-1 px-1.5 sm:px-2.5 py-1 sm:py-1.5 text-xs font-medium border border-slate-200 rounded-lg bg-white dark:bg-slate-800 dark:border-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none [&::-webkit-calendar-picker-indicator]:p-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60"
                                                    />
                                                    <span className="text-xs text-slate-400 font-medium shrink-0 px-0.5">{t("label_to", "to")}</span>
                                                    <input
                                                        type="time"
                                                        value={schedule.entry_end_time}
                                                        onChange={(e) => handleScheduleChange(schedule.originalIndex, 'entry_end_time', e.target.value)}
                                                        className="min-w-0 flex-1 px-1.5 sm:px-2.5 py-1 sm:py-1.5 text-xs font-medium border border-slate-200 rounded-lg bg-white dark:bg-slate-800 dark:border-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none [&::-webkit-calendar-picker-indicator]:p-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors shrink-0"
                                                        onClick={() => handleRemoveSchedule(schedule.originalIndex)}
                                                        title="Remove Shift"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                            
                            {activeDays.length === 0 && (
                                <p className="text-center text-xs text-slate-500 dark:text-slate-400 py-4">No schedules added yet. Please add a day.</p>
                            )}
                        </div>
                    </div>
                )}

                <div className="sticky bottom-0 -mx-4 -mb-6 mt-6 px-4 py-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2.5 z-20 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
                    <Button 
                        variant="secondary_outline" 
                        type="button" 
                        onClick={onClose} 
                        disabled={loading}
                        className="flex-1 sm:flex-initial"
                    >
                        {translations[language]?.common?.btn_cancel || translations.en?.common?.btn_cancel || "Cancel"}
                    </Button>
                    <Button 
                        variant="primary" 
                        type="submit" 
                        disabled={loading}
                        loading={loading}
                        className="flex-1 sm:flex-initial"
                    >
                        {loading ? t("btn_saving", "Saving...") : (providerToEdit ? t("btn_update_provider", "Update Provider") : t("btn_save_provider", "Save Provider"))}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default AddEditVisitorServiceProviderModal;
